import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RefreshCw, ExternalLink, X, ClipboardList, Link2, PencilLine } from "lucide-react";

const MARKETS = [
  { code: "US", label: "United States" },
  { code: "CA", label: "Canada" },
];

const PILLAR_BY_TOPICS = {
  recalls: "roofing", "product-safety": "roofing", appliances: "repairs-maintenance", heaters: "electrical",
  "severe-weather": "roofing", hurricanes: "water-damage", freezes: "plumbing", hail: "roofing", flooding: "water-damage",
  floods: "water-damage", tornadoes: "roofing", "disaster-prep": "water-damage", "disaster-recovery": "water-damage", earthquakes: "roofing",
};

function guessPillar(topics) {
  for (const t of topics || []) {
    if (PILLAR_BY_TOPICS[t]) return PILLAR_BY_TOPICS[t];
  }
  return "roofing";
}

function priorityBadgeClass(score) {
  if (score >= 75) return "bg-emerald-100 text-emerald-700 border-emerald-300";
  if (score >= 50) return "bg-amber-100 text-amber-700 border-amber-300";
  return "bg-slate-100 text-slate-500 border-slate-300";
}

function RelatedContent({ relatedContent }) {
  if (!relatedContent || relatedContent.action === "new_page" || !relatedContent.targets?.length) {
    return <span className="text-xs text-slate-300">—</span>;
  }
  const isUpdate = relatedContent.action === "update_existing";
  return (
    <div className="space-y-1" data-testid="newsroom-related-content">
      {relatedContent.targets.map((t, idx) => (
        <div key={idx} className="flex items-center gap-1 text-xs">
          {isUpdate ? <PencilLine className="w-3 h-3 text-amber-600 flex-shrink-0" /> : <Link2 className="w-3 h-3 text-blue-500 flex-shrink-0" />}
          {t.type === "article" ? (
            <Link to={`/admin/articles/${t.slug}/edit`} className="text-blue-600 hover:underline truncate max-w-[160px]" title={t.title}>{t.title}</Link>
          ) : (
            <Link to="/admin/categories" className="text-blue-600 hover:underline truncate max-w-[160px]" title={t.title}>{t.title} hub</Link>
          )}
        </div>
      ))}
    </div>
  );
}

export default function AdminNewsroom() {
  const [market, setMarket] = useState("US");
  const [items, setItems] = useState(null);
  const [sources, setSources] = useState([]);
  const [polling, setPolling] = useState(false);
  const [generatingId, setGeneratingId] = useState(null);
  const navigate = useNavigate();

  const load = (m) => {
    setItems(null);
    api.get(`/admin/newsroom/queue?status=new&market=${m}`).then((res) => setItems(res.data.items)).catch(() => {
      setItems([]);
      toast.error("Failed to load newsroom queue");
    });
    api.get(`/admin/newsroom/sources?market=${m}`).then((res) => setSources(res.data)).catch(() => setSources([]));
  };
  useEffect(() => { load(market); }, [market]);

  const pollNow = async () => {
    setPolling(true);
    try {
      const { data } = await api.post("/admin/newsroom/poll-now");
      const total = Object.values(data.results || {}).reduce((a, b) => a + b, 0);
      toast.success(total > 0 ? `Found ${total} new item(s)` : "No new items right now");
      load(market);
    } catch {
      toast.error("Polling failed");
    } finally {
      setPolling(false);
    }
  };

  const dismiss = async (id) => {
    await api.post(`/admin/newsroom/queue/${id}/dismiss`);
    setItems((prev) => prev.filter((i) => i.id !== id));
    toast.success("Dismissed");
  };

  const generateBrief = async (item) => {
    setGeneratingId(item.id);
    try {
      await api.post("/admin/newsroom/briefs/generate", {
        source_type: "newsroom_item",
        source_id: item.id,
        topic: item.title,
        pillar: guessPillar(item.topics),
        content_type: "news",
        context: item.summary || "",
        market,
      });
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      toast.success("Brief generated");
      navigate("/admin/briefs");
    } catch {
      toast.error("Brief generation failed");
    } finally {
      setGeneratingId(null);
    }
  };

  return (
    <div data-testid="admin-newsroom-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-serif-heading font-bold text-navy">Newsroom Queue</h1>
        <Button onClick={pollNow} disabled={polling} data-testid="newsroom-poll-now-button" className="bg-gold text-navy hover:bg-gold/90">
          <RefreshCw className={`w-4 h-4 mr-2 ${polling ? "animate-spin" : ""}`} /> {polling ? "Polling..." : "Poll Now"}
        </Button>
      </div>

      <Tabs value={market} onValueChange={setMarket} className="mb-4">
        <TabsList data-testid="newsroom-market-tabs">
          {MARKETS.map((m) => (
            <TabsTrigger key={m.code} value={m.code} data-testid={`newsroom-market-tab-${m.code}`}>{m.label}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="bg-white rounded-lg border border-slate-200 p-4 mb-6 flex flex-wrap gap-4 text-sm" data-testid="newsroom-sources-status">
        {sources.map((s) => (
          <div key={s.slug} className="flex items-center gap-2">
            <span className="font-medium text-navy">{s.name}</span>
            <span className="text-slate-400">
              {s.last_poll_error ? <span className="text-red-500">error</span> : s.last_checked_at ? `checked ${new Date(s.last_checked_at).toLocaleTimeString()}` : "not yet polled"}
            </span>
          </div>
        ))}
        {sources.length === 0 && <span className="text-slate-400">No sources configured for {market} yet.</span>}
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Priority</TableHead><TableHead>Title</TableHead><TableHead>Source</TableHead><TableHead>Tier</TableHead>
              <TableHead>Related Page</TableHead>
              <TableHead>Published</TableHead><TableHead>Discovered</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items?.map((item) => (
              <TableRow key={item.id} data-testid={`newsroom-item-${item.id}`}>
                <TableCell>
                  <Badge className={`border ${priorityBadgeClass(item.priority_score ?? 0)}`} data-testid={`newsroom-priority-${item.id}`}>
                    {item.priority_score ?? "-"}
                  </Badge>
                </TableCell>
                <TableCell className="font-medium text-navy max-w-md">
                  {item.title}
                  {item.regulated && <Badge variant="destructive" className="ml-2 text-[10px]">Regulated</Badge>}
                  {item.url && (
                    <a href={item.url} target="_blank" rel="noreferrer" className="ml-2 text-slate-400 inline-flex align-middle">
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant="secondary">{item.source_name}</Badge>
                  {item.cluster_source_count > 1 && (
                    <span
                      className="ml-1.5 text-[11px] text-slate-400 cursor-help"
                      title={(item.cluster_sources || []).map((s) => `${s.source_name}: ${s.title}`).join("\n")}
                      data-testid={`newsroom-cluster-count-${item.id}`}
                    >
                      +{item.cluster_source_count - 1} more source{item.cluster_source_count - 1 > 1 ? "s" : ""}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-sm text-slate-500">Tier {item.tier}</TableCell>
                <TableCell><RelatedContent relatedContent={item.related_content} /></TableCell>
                <TableCell className="text-xs text-slate-400">{item.published_at ? new Date(item.published_at).toLocaleDateString() : "-"}</TableCell>
                <TableCell className="text-xs text-slate-400">{new Date(item.discovered_at).toLocaleString()}</TableCell>
                <TableCell className="text-right space-x-2">
                  <Button size="sm" variant="outline" onClick={() => generateBrief(item)} disabled={generatingId === item.id} data-testid={`newsroom-brief-button-${item.id}`}>
                    <ClipboardList className="w-3.5 h-3.5 mr-1" /> {generatingId === item.id ? "Generating..." : "Generate Brief"}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => dismiss(item.id)} data-testid={`newsroom-dismiss-button-${item.id}`}>
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {items?.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-8">No new items in the {market} queue. Click "Poll Now" to check sources.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
